/**
 * Design system component: PolicyBadge
 *
 * Inline pill badge that indicates the circulation policy for an item.
 * Always visible on load — never hidden behind a tap — so borrowers can see
 * the return requirement without any interaction.
 *
 *   - `RETURN_TO_OWNER_REQUIRED` → amber badge with "↩ Return to owner"
 *   - `FREE_CIRCULATION_ALLOWED` → green badge with "⇄ Free circulation"
 *
 * Accent orange is deliberately not used here; policy badges use semantic
 * warning/success colours to distinguish the two modes from primary CTAs.
 */
import { T } from '@/lib/tokens';
import { Undo2, ArrowLeftRight } from 'lucide-react';

interface PolicyBadgeProps {
  policy: 'RETURN_TO_OWNER_REQUIRED' | 'FREE_CIRCULATION_ALLOWED';
}

/**
 *
 * @param root0
 * @param root0.policy
 */
export function PolicyBadge({ policy }: PolicyBadgeProps) {
  const isReturn = policy === 'RETURN_TO_OWNER_REQUIRED';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 9px',
        borderRadius: 6,
        background: isReturn ? T.warningSoft : T.successSoft,
        color: isReturn ? T.warning : T.success,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.03em',
        border: `1px solid ${isReturn ? T.warningBorder : T.successBorder}`,
        whiteSpace: 'nowrap',
      }}
    >
      {isReturn ? (
        <>
          <Undo2 size={12} /> Return to owner
        </>
      ) : (
        <>
          <ArrowLeftRight size={12} /> Free circulation
        </>
      )}
    </span>
  );
}
